import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '../stores/sessionStore.js';
import { useSettingsStore } from '../stores/settingsStore.js';
import { useWalletStore } from '../stores/walletStore.js';

const BLUR_THRESHOLD_MS = 30_000;
const IDLE_CHECK_INTERVAL_MS = 10_000;

/**
 * Auto-lock driver. Watches user activity and locks the wallet after
 * `settings.autoLockMinutes` of inactivity, or when the tab is hidden
 * for more than 30 seconds (blur or visibilitychange — mobile Safari
 * and the Telegram WebView fire different events, so we listen to
 * both). Called once from the router Layout wrapper so every screen
 * benefits from it.
 *
 * Invariants:
 *  - Does nothing when no wallet is unlocked (unlockedAt === null)
 *  - Does nothing when autoLockMinutes === 0 (Off)
 *  - On lock: clears unlockedAt (not failedAttempts — see sessionStore.lock)
 *    and navigates to /unlock if a wallet exists
 */
export function useAutoLock(): void {
  const navigate = useNavigate();
  const unlockedAt = useSessionStore((s) => s.unlockedAt);
  const lastActivityAt = useSessionStore((s) => s.lastActivityAt);
  const lock = useSessionStore((s) => s.lock);
  const markActivity = useSessionStore((s) => s.markActivity);
  const autoLockMinutes = useSettingsStore((s) => s.autoLockMinutes);
  const hasWallet = useWalletStore((s) => s.wallets.length > 0);

  // Activity tracking on any user interaction.
  useEffect(() => {
    if (!unlockedAt) return;
    const handler = (): void => markActivity();
    window.addEventListener('pointerdown', handler);
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('pointerdown', handler);
      window.removeEventListener('keydown', handler);
    };
  }, [unlockedAt, markActivity]);

  // Idle lock timer.
  useEffect(() => {
    if (!unlockedAt || autoLockMinutes === 0) return;
    const interval = setInterval(() => {
      const idleMs = Date.now() - lastActivityAt;
      if (idleMs > autoLockMinutes * 60_000) {
        lock();
        if (hasWallet) navigate('/unlock');
      }
    }, IDLE_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [unlockedAt, lastActivityAt, autoLockMinutes, lock, navigate, hasWallet]);

  // Blur + visibilitychange lock. Tab hidden for >30s → lock on return.
  useEffect(() => {
    if (!unlockedAt) return;
    let hiddenAt: number | null = null;

    const onHide = (): void => {
      hiddenAt = Date.now();
    };
    const onShow = (): void => {
      if (hiddenAt !== null && Date.now() - hiddenAt > BLUR_THRESHOLD_MS) {
        lock();
        if (hasWallet) navigate('/unlock');
      }
      hiddenAt = null;
    };
    const onVis = (): void => {
      if (document.visibilityState === 'hidden') onHide();
      else onShow();
    };

    window.addEventListener('blur', onHide);
    window.addEventListener('focus', onShow);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('blur', onHide);
      window.removeEventListener('focus', onShow);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [unlockedAt, lock, navigate, hasWallet]);
}
